const MOBILE_BREAKPOINT = 768;
const SWIPE_THRESHOLD = 50;
const WHEEL_COOLDOWN = 500;

export default class {
  constructor(element) {
    this.element = element;
    this.images = element.querySelectorAll(".carousel__image");
    this.container = element.querySelector(".carousel__images");
    this.dots = element.querySelectorAll(".carousel__dot");
    this.prevButton = element.querySelector(".carousel__prev-button");
    this.nextButton = element.querySelector(".carousel__next-button");
    this.showButtons = document.querySelectorAll(
      `button[data-carousel="#${element.id}"]`,
    );
    this.closeButton = element.querySelector(".carousel__close-button");

    this.currentIndex = undefined;
    this.lastIndex = this.images.length - 1;
    this.isWheelBlocked = false;
    this.touchStartPos = 0;
    this.isSwiping = false;

    this.isVisible = false;

    this.setupEventListeners();
  }

  show() {
    if (this.isVisible) return;

    this.isVisible = true;
    this.element.classList.remove("is-hidden");
    this.element.classList.add("is-visible");

    requestAnimationFrame(() => {
      this.scrollToImage(0);
    });
  }

  hide() {
    if (!this.isVisible) return;

    this.isVisible = false;
    this.element.classList.remove("is-visible");
    this.element.classList.add("is-hidden");

    this.scrollToImage(0);
  }

  /*--< ОСНОВНАЯ ЛОГИКА >------------------------------------*/

  scrollToImage(index) {
    if (!this.isValidIndex(index) || this.currentIndex === index) return;

    this.currentIndex = index;
    this.updateControls();
    this.updateDots();
    this.scrollContainerToImage(index);
  }

  isValidIndex(index) {
    return index >= 0 && index <= this.lastIndex;
  }

  isImageFullyVisible(index) {
    const image = this.images[index];
    const imageRect = image.getBoundingClientRect();
    const containerRect = this.container.getBoundingClientRect();

    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;

    if (isMobile) {
      const visibleTop = Math.max(containerRect.top, imageRect.top);
      const visibleBottom = Math.min(containerRect.bottom, imageRect.bottom);
      const visibleHeight = visibleBottom - visibleTop;

      return visibleHeight >= imageRect.height - 1; // Для погрешности
    } else {
      const visibleLeft = Math.max(containerRect.left, imageRect.left);
      const visibleRight = Math.min(containerRect.right, imageRect.right);
      const visibleWidth = visibleRight - visibleLeft;

      return visibleWidth >= imageRect.width - 1;
    }
  }

  /*--< ОБНОВЛЕНИЕ UI >--------------------------------------*/

  updateControls() {
    this.prevButton.disabled = this.currentIndex === 0;
    this.nextButton.disabled = this.currentIndex === this.lastIndex;
  }

  updateDots() {
    this.dots.forEach((dot) => dot.classList.remove("is-active"));
    this.dots[this.currentIndex]?.classList.add("is-active");
  }

  /*--< ПРОКРУТКА >------------------------------------------*/

  scrollContainerToImage(index) {
    const targetImage = this.images[index];
    const containerRect = this.container.getBoundingClientRect();
    const imageRect = targetImage.getBoundingClientRect();

    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
    const scrollOptions = this.calculateScrollPosition(
      containerRect,
      imageRect,
      isMobile,
    );

    this.container.scrollTo({
      ...scrollOptions,
      behavior: "smooth",
    });
  }

  calculateScrollPosition(containerRect, imageRect, isMobile) {
    if (isMobile) {
      const relativeTop =
        imageRect.top - containerRect.top + this.container.scrollTop;
      const imageHeight = imageRect.height;
      const containerHeight = this.container.clientHeight;

      return {
        top: relativeTop - containerHeight / 2 + imageHeight / 2,
      };
    }

    const relativeLeft =
      imageRect.left - containerRect.left + this.container.scrollLeft;
    const imageWidth = imageRect.width;
    const containerWidth = this.container.clientWidth;

    return {
      left: relativeLeft - containerWidth / 2 + imageWidth / 2,
    };
  }

  /*--< НАВИГАЦИЯ >------------------------------------------*/

  goToPrev() {
    let scrolled = false;
    for (let i = this.currentIndex - 1; i > 0; i--) {
      if (!this.isImageFullyVisible(i)) {
        this.scrollToImage(i);
        scrolled = true;
        break;
      }
    }

    if (!scrolled && this.currentIndex !== 0) {
      this.scrollToImage(0);
    }
  }

  goToNext() {
    for (let i = this.currentIndex + 1; i <= this.lastIndex; i++) {
      if (!this.isImageFullyVisible(i)) {
        this.scrollToImage(i);
        break;
      }
    }

    // if (this.currentIndex < this.lastIndex) {
    //     this.scrollToImage(this.currentIndex + 1);
    //   }
  }

  goToIndex(index) {
    this.scrollToImage(index);
  }

  /*--< ОБРАБОТЧИКИ СОБЫТИЙ >--------------------------------*/

  setupEventListeners() {
    this.showButtons.forEach((showButton) =>
      showButton.addEventListener("click", () => {
        this.show();
      }),
    );

    this.closeButton.addEventListener("click", () => this.hide());

    this.container.addEventListener("scroll", (e) => e.stopPropagation());

    this.dots.forEach((dot, index) => {
      dot.addEventListener("click", () => this.goToIndex(index));
    });

    this.prevButton.addEventListener("click", () => this.goToPrev());
    this.nextButton.addEventListener("click", () => this.goToNext());

    this.setupWheelHandler();
    this.setupTouchHandlers();
    this.setupResizeHandler();
  }

  setupWheelHandler() {
    this.container.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        if (this.isWheelBlocked) return;

        this.isWheelBlocked = true;
        const direction = e.deltaY > 0 ? 1 : -1;

        if (e.deltaY > 0) {
          this.goToNext();
        } else {
          this.goToPrev();
        }

        setTimeout(() => {
          this.isWheelBlocked = false;
        }, WHEEL_COOLDOWN);
      },
      { passive: false },
    );
  }

  setupTouchHandlers() {
    const getTouchPos = (e) => {
      const touch = e.changedTouches[0];
      return window.innerWidth > MOBILE_BREAKPOINT
        ? touch.screenX
        : touch.screenY;
    };

    this.container.addEventListener(
      "touchstart",
      (e) => {
        this.touchStartPos = getTouchPos(e);
        this.isSwiping = true;
      },
      { passive: true },
    );

    this.container.addEventListener(
      "touchmove",
      (e) => {
        if (!this.isSwiping) return;
        e.preventDefault();
      },
      { passive: false },
    );

    this.container.addEventListener(
      "touchend",
      (e) => {
        if (!this.isSwiping) return;
        this.isSwiping = false;

        const touchEndPos = getTouchPos(e);
        const delta = this.touchStartPos - touchEndPos;

        if (Math.abs(delta) > SWIPE_THRESHOLD) {
          if (delta > 0) {
            this.goToNext();
          } else {
            this.goToPrev();
          }
        }
      },
      { passive: true },
    );
  }

  setupResizeHandler() {
    const handleResize = () => {
      if (this.currentIndex !== undefined) {
        this.scrollContainerToImage(this.currentIndex);
      }
    };

    let resizeTimeout;
    window.addEventListener("resize", () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 150);
    });
  }
}
